<template>
	<AppLayout
		:title="boardWorkgroupMember.user.full_name"
		:subtitle="boardWorkgroupMember.area.name"
		:return-label="boardArea.name"
		:return-url="route('association.boards.areas.workgroup-members.index', {board: board.id, boardArea: boardArea.id})"
	>
		<Modal id="create-sanction-modal">
			<template #title>{{ trans('association.sanctions.create.title') }}</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<LabeledObject full>
					<template #label>{{ trans('association.sanctions.begins_at') }}</template>
					<IconDateTimeInput v-model="newSanction.begins_at" icon-class="fa-solid fa-hourglass-start" full/>
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('association.sanctions.ends_at') }}</template>
					<IconDateTimeInput v-model="newSanction.ends_at" icon-class="fa-solid fa-hourglass-end" full/>
				</LabeledObject>
				<LabeledObject class="lg:col-span-2">
					<template #label>{{ trans('association.sanctions.reason') }}</template>
					<textarea v-model="newSanction.reason"/>
				</LabeledObject>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template v-if="boardWorkgroupMember.expelled_at === null && boardWorkgroupMember.finished_at === null && boardWorkgroupMember.upgraded_at === null" #actions>
			<Link :as="SoftButton" :color="Color.Red" :href="route('association.boards.areas.workgroup-members.expel.index', {board: board.id, boardArea: boardArea.id, boardWorkgroupMember: boardWorkgroupMember.id})">
				<i class="fa-solid fa-ban mr-2"/>
				{{ trans('association.boards.areas.members.workgroup.expel.title') }}
			</Link>
			<Link :as="SoftButton" :color="Color.Lime" :href="route('association.boards.areas.workgroup-members.upgrade.index', {board: board.id, boardArea: boardArea.id, boardWorkgroupMember: boardWorkgroupMember.id})">
				<i class="fa-solid fa-level-up mr-2"/>
				{{ trans('association.boards.areas.members.workgroup.upgrade.title') }}
			</Link>
			<Link :as="SoftButton" :color="Color.Teal" :href="route('association.boards.areas.workgroup-members.finalize.index', {board: board.id, boardArea: boardArea.id, boardWorkgroupMember: boardWorkgroupMember.id})">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('association.boards.areas.members.workgroup.finalize.title') }}
			</Link>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-sanction-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('association.sanctions.create.title') }}
			</PrimaryButton>
		</template>
		<Card>
			<SanctionableDetails :sanctionable="boardWorkgroupMember"/>
		</Card>
		<Card :title="trans('association.sanctions.title')">
			<Empty v-if="sanctions.length === 0"/>
			<List v-for="sanction in sanctions" :key="`sanction-${sanction.id}`">
				<ListItem :title="`${dayjs(sanction.begins_at).format(trans('general.dayjs.datetime'))} - ${dayjs(sanction.ends_at).format(trans('general.dayjs.datetime'))}`" :subtitle="sanction.reason"/>
			</List>
		</Card>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import {type Sanction} from '@/Types/Association/Sanction'
import {type Board} from '@/Types/Association/Boards/Board'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type BoardArea} from '@/Types/Association/Boards/BoardArea'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type BoardWorkgroupMember} from '@/Types/Association/Boards/BoardWorkgroupMember'
import SanctionableDetails from '@/Components/Association/Sanctions/SanctionableDetails.vue'

const props = defineProps<{
	board:Board,
	boardArea:BoardArea,
	sanctions:Array<Sanction>,
	boardWorkgroupMember:BoardWorkgroupMember,
}>()

const board:Ref<Board> = ref(props.board)
const working:Ref<boolean> = ref(false)
const boardArea:Ref<BoardArea> = ref(props.boardArea)
const boardWorkgroupMember:Ref<BoardWorkgroupMember> = ref(props.boardWorkgroupMember)
const sanctions:Ref<Array<Sanction>> = ref(props.sanctions)
const newSanction:Ref<Sanction> = ref({
	sanctionable_type: boardWorkgroupMember.value.type,
	sanctionable_id: boardWorkgroupMember.value.id,
	begins_at: '',
	ends_at: '',
	reason: '',
} as Sanction)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.sanctions.store'), newSanction.value).then(({data}:{data:Sanction}) => {
			showSuccess(trans('general.inserted'))
			sanctions.value.unshift(data)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>